/* eslint-disable max-len */
import { useMemo, useState } from "react";
import {
  useTranslate,
  useGetList,
  useDataProvider,
  useNotify
} from "react-admin";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import * as User from "providers/user";
import CustomHeaderActions from "./CustomHeaderActions";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  IconButton,
  TextField
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import { SortableList } from "./SortableList";
import { CustomButton } from "./CustomButton";
import { muiColors } from "themes/colors";
import MenuDndSectionItem from "./MenuDndSectionItem";

const validationSchema = yup
  .object()
  .shape({
    name: yup.string().required("Menu name required."),
    menu_sections: yup
      .array()
      .min(1, "At least one section is required.")
      .of(
        yup.object().shape({
          name: yup.string().required("Section name is required."),
          services: yup
            .array()
            .min(1, "At least one service is required.")
            .of(yup.string())
            .required("At least one service is required.")
        })
      )
      .required("At least one section is required.")
  })
  .required();

export default function MenuCreate() {
  const { permissions } = User.useUser();
  const translate = useTranslate();
  const notify = useNotify();
  const navigate = useNavigate();
  const dataProvider = useDataProvider();

  const [name, setName] = useState("");
  const [items, setItems] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [expandedAccordions, setExpandedAccordions] = useState(new Set());

  const { data: allServices } = useGetList("Service", {
    filter: {
      company_id: permissions?.companyId,
      is_addon: false
    },
    sort: { field: "name", order: "ASC" },
    pagination: { page: 1, perPage: 500 }
  });

  const serviceChoices = allServices?.map((item) => ({
    id: item.id,
    name: item.name,
    price: item.price,
    description: item.description
  }));

  const isDirty = useMemo(() => {
    if (name.trim() !== "" || items.length > 0) return true;
    return false;
  }, [name, items]);

  const isDndHidden = expandedAccordions?.size > 0;

  const handleAccordionChange = (itemId: string, expanded: boolean) => {
    setExpandedAccordions((prev) => {
      const newSet = new Set(prev);
      if (expanded) {
        newSet.add(itemId);
      } else {
        newSet.delete(itemId);
      }
      return newSet;
    });
  };

  const removeSection = (itemId: string) => {
    setItems((prev: any) => prev.filter((i: any) => i?.id !== itemId));
    setExpandedAccordions((prev) => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
  };

  const submit = async () => {
    setIsLoading(true);
    const formData = {
      name,
      menu_sections: items.map((section: any) => ({
        name: section.name,
        services: (section.menu_section_services || []).map((service: any) =>
          service.id ? service.id : service
        )
      }))
    };
    try {
      await validationSchema.validate(formData, { abortEarly: false });
    } catch (err) {
      if (err.inner && err.inner.length) {
        const errorMessages = err.inner
          .map((e: any) => {
            const path = e.path;
            const match = path.match(/menu_sections\[(\d+)\]/);
            if (match) {
              const sectionNumber = parseInt(match[1], 10) + 1;
              return `Section ${sectionNumber}: ${e.message}`;
            }
            return e.message;
          })
          .join("\n");
        notify(
          <Alert
            severity="error"
            sx={{
              whiteSpace: "pre-line",
              bgcolor: muiColors.error,
              color: "#fff !important"
            }}
          >
            {errorMessages}
          </Alert>,
          { type: "warning", autoHideDuration: 7000 }
        );
      } else {
        notify(`${err.message}`, { type: "warning", autoHideDuration: 7000 });
      }
      setIsLoading(false);
      return;
    }
    try {
      const newMenu = await dataProvider.create("Menu", {
        data: {
          name,
          company: { id: permissions.companyId }
        }
      });
      const newMenuId = newMenu?.data?.id;
      if (!newMenuId) {
        notify("Unable to create menu. Missing Menu ID.", { type: "error" });
        setIsLoading(false);
        return;
      }
      for (let index = 0; index < items?.length; index++) {
        const section: any = items[index];
        const newSectionData = {
          name: section?.name,
          company: { id: permissions?.companyId },
          menu: { id: newMenuId },
          section_position: index + 1,
          menu_section_services: (section?.menu_section_services || []).map(
            (serviceItem: any, idx: number) => ({
              service: {
                id: serviceItem?.id ?? serviceItem
              },
              section_position: idx + 1
            })
          )
        };
        await dataProvider.create("MenuSection", { data: newSectionData });
      }
      setIsLoading(false);
      navigate("/dashboard/Menu");
    } catch (error) {
      notify("There was an error creating the menu", { type: "error" });
      setIsLoading(false);
    }
  };

  return (
    <Box
      className="menu-dnd-container"
      sx={{
        "& .MuiAccordion-root": {
          boxShadow: "none !important",
          backgroundColor: "rgba(0, 0, 0, 0.04) !important"
        },
        "& .MuiAccordionSummary-root": {
          minHeight: "36px !important",
          paddingLeft: "0px !important"
        },
        "& .MuiAccordionDetails-root": {
          padding: "8px 16px 8px 30px !important"
        },
        "&.Mui-expanded": {
          minHeight: "36px !important",
          margin: "0px !important"
        },
        "& .MuiAccordionSummary-content": {
          margin: "0px !important"
        }
      }}
    >
      <CustomHeaderActions title={translate("pagenames.menu")} />
      <TextField
        variant="filled"
        label={translate("common.name")}
        data-cy="description-input"
        value={name}
        onChange={(e) => setName(e.target.value)}
        fullWidth
        sx={{
          marginBottom: "16px !important",
          "& .MuiFilledInput-input": {
            paddingTop: "22px",
            paddingBottom: "5px",
            fontWeight: 400
          },
          "& .MuiFormHelperText-root": {
            display: "none"
          }
        }}
      />
      <div className="text-[14px] h-[25px] text-[rgba(48,47,46,0.6)]">
        {translate("services.menu_sections")}:
      </div>
      <SortableList
        items={items}
        onChange={setItems}
        renderItem={(item: any, itemIndex: number) => (
          <SortableList.Item id={item.id}>
            <Accordion
              key={item.id}
              expanded={expandedAccordions.has(item.id)}
              onChange={(_, expanded) =>
                handleAccordionChange(item.id, expanded)
              }
              sx={{ width: "100%" }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
                sx={{
                  "&.Mui-expanded": {
                    minHeight: "36px !important",
                    margin: "0px !important"
                  }
                }}
              >
                {!isDndHidden ? (
                  <SortableList.DragHandle />
                ) : (
                  <div className="w-[31px] h-[42px]" />
                )}
                <div className="w-full text-[16px] font-[600] flex items-center">
                  <div className="min-w-[24px] ml-2">
                    {itemIndex !== undefined && itemIndex + 1 + ". "}
                  </div>
                  <input
                    className="border-none max-w-[320px] w-full outline-none bg-transparent text-[16px] font-[500] focus:!border-mono-primary focus:!shadow-none focus:ring-mono-primary"
                    onClick={(e) => e.stopPropagation()}
                    placeholder={translate("menusections.section_name")}
                    value={(item?.name as string) || ""}
                    onChange={(e) => {
                      setItems((prev: any) =>
                        prev?.map((i: any) =>
                          i.id === item?.id ? { ...i, name: e.target.value } : i
                        )
                      );
                    }}
                  />
                  <IconButton
                    sx={{
                      marginLeft: "16px",
                      width: "32px",
                      height: "32px"
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSection(item.id);
                    }}
                  >
                    <CloseIcon
                      sx={{
                        width: "24px",
                        height: "24px",
                        color: muiColors.error
                      }}
                    />
                  </IconButton>
                </div>
              </AccordionSummary>
              <AccordionDetails
                sx={{ marginTop: "16px" }}
                className="menu-sections-container"
              >
                <MenuDndSectionItem
                  menu_section={item}
                  serviceChoices={serviceChoices}
                  onChangeServices={(newServices: any, menuSectionId: any) => {
                    setItems((prev: any) =>
                      prev.map((i: any) =>
                        i.id === menuSectionId
                          ? { ...i, menu_section_services: newServices }
                          : i
                      )
                    );
                  }}
                />
              </AccordionDetails>
            </Accordion>
          </SortableList.Item>
        )}
      />
      <CustomButton
        text={translate("menusections.add_menu_section")}
        variant="secondary"
        onClick={() => {
          setItems((prev: any) => [
            ...prev,
            {
              id: Math.random().toString(),
              name: "",
              menu_section_services: []
            }
          ]);
        }}
        sx={{ marginTop: "16px", borderColor: "#B8722A", color: "#B8722A" }}
      />
      <CustomButton
        text={translate("common.save")}
        variant="primary"
        disabled={!isDirty || isLoading}
        onClick={submit}
        sx={{ marginTop: "32px" }}
      />
    </Box>
  );
}
